import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { lt, useUrlSyncedTableState } from '@mews/framework';
import { Table, TableActiveColumnFilters, TableHeader, TableProvider, useFullRowSelection } from '@optimus-web/core';
import {
    GeneralPricingMode,
    PortfolioProductOverviewEntriesFilters,
    PortfolioProductOverviewEntry,
} from '@mews-commander/data-access';
import { tableEmptyState } from '../../_shared/tableEmptyState';
import { useGetLocalProductOverviewEntries } from '../../../queries/LocalProductsTable';
import { useProductsWithTaxation } from '../../../hooks/useProductsWithTaxation';
import { EnterprisesProductContextProvider } from '../../../context/EnterprisesProductContext';
import { BulkActions } from './BulkActions';
import { useLocalProductTableColumns } from './columns';

export const LocalProductTable = () => {
    const { pricing: enterprisePricing } = useCurrentEnterprise();

    const isIncludeTax = enterprisePricing === GeneralPricingMode.Gross;

    const { columns } = useLocalProductTableColumns({ isIncludeTax });

    const [tableState, setTableState] = useUrlSyncedTableState<
        PortfolioProductOverviewEntry,
        PortfolioProductOverviewEntriesFilters
    >({
        columns,
        pageSize: 25,
    });

    const { data: localProductsEntries, isFetching } = useGetLocalProductOverviewEntries(tableState);

    const { mappedData } = useProductsWithTaxation(localProductsEntries?.data || []);

    const { selectedRowsData, rowSelection, setRowSelection, resetRowSelection } = useFullRowSelection(
        localProductsEntries?.data
    );

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
            <TableHeader title={lt('Products')} subTitle={localProductsEntries?.totalRowCount || '0'} />
            <TableActiveColumnFilters />
            <EnterprisesProductContextProvider>
                <BulkActions selectedRows={selectedRowsData} resetRowSelection={resetRowSelection} />
                <Table
                    loading={isFetching}
                    uniqueRowId="id"
                    data={mappedData ?? []}
                    amountOfRows={localProductsEntries?.totalRowCount ?? 0}
                    fixedFirstColumn
                    fixedLastColumn
                    emptyState={tableEmptyState}
                    rowSelection={{
                        rowSelection,
                        onRowSelectionChange: setRowSelection,
                    }}
                />
            </EnterprisesProductContextProvider>
        </TableProvider>
    );
};
